import type { ApiErrorBody, ApiSuccess, Paginated } from "./contracts";
import { endpoints } from "./contracts";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
let csrfToken: string | null = null;

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly correlationId = "",
    public readonly fieldErrors?: Record<string, string[]>,
    public readonly current?: unknown,
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
  }
}

function correlationId() {
  return globalThis.crypto?.randomUUID?.() ?? `web-${Date.now()}`;
}

async function getCsrfToken() {
  if (csrfToken) return csrfToken;
  const response = await fetch(`${API_BASE}${endpoints.auth.csrf}`, {
    credentials: "include",
    headers: { Accept: "application/json", "X-Correlation-ID": correlationId() },
  });
  if (!response.ok) throw await toApiError(response);
  const body = (await response.json()) as ApiSuccess<{ csrfToken: string }> | { csrfToken: string };
  csrfToken = "data" in body ? body.data.csrfToken : body.csrfToken;
  return csrfToken;
}

async function toApiError(response: Response) {
  let body: ApiErrorBody | null = null;
  try { body = (await response.json()) as ApiErrorBody; } catch { /* safe generic error */ }
  const detail = body?.error;
  return new ApiError(
    detail?.message ?? "请求暂时无法完成，请稍后重试。",
    response.status,
    detail?.code ?? `HTTP_${response.status}`,
    detail?.correlationId ?? response.headers.get("X-Correlation-ID") ?? "",
    detail?.fieldErrors,
    detail?.current,
    detail?.retryAfterSeconds,
  );
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  raw?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const mutating = !["GET", "HEAD", "OPTIONS"].includes(method);
  const headers = new Headers(options.headers);
  headers.set("Accept", options.raw ? "*/*" : "application/json");
  headers.set("X-Correlation-ID", correlationId());
  if (options.body !== undefined) headers.set("Content-Type", "application/json");
  if (mutating) headers.set("X-CSRF-Token", await getCsrfToken());

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      method,
      headers,
      credentials: "include",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch {
    throw new ApiError("无法连接服务器，请检查网络后重试。", 0, "NETWORK_ERROR");
  }

  if (!response.ok) {
    const error = await toApiError(response);
    if (response.status === 401) {
      csrfToken = null;
      window.dispatchEvent(new CustomEvent("workbench:session-expired"));
    }
    throw error;
  }
  if (options.raw) return response as T;
  if (response.status === 204) return undefined as T;
  const body = (await response.json()) as ApiSuccess<T> | T;
  return (typeof body === "object" && body !== null && "data" in body ? (body as ApiSuccess<T>).data : body) as T;
}

export function withQuery(path: string, values: Record<string, string | number | boolean | null | undefined>) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) if (value !== undefined && value !== null && value !== "") query.set(key, String(value));
  const text = query.toString();
  return text ? `${path}?${text}` : path;
}

export async function list<T>(path: string, params: Record<string, string | number | boolean | null | undefined> = {}) {
  const response = await request<Paginated<T> | T[]>(withQuery(path, { page: 1, pageSize: 100, ...params }));
  return Array.isArray(response) ? response : response.data;
}

export function resetCsrf() { csrfToken = null; }
